import { useId } from 'react'

/**
 * Console charts — the three shapes the redesign uses: a line chart, a
 * stacked proportion bar and a funnel row. No rings or donuts anywhere in the
 * adult tiers (the brief rules them out: a ring hides the comparison a bar
 * makes obvious).
 *
 * ⚠️ DATA IN, PIXELS OUT. Each takes the numbers a screen already has; none
 * of them fills a gap with a guess. An empty series draws the frame and says
 * so rather than a flat line that reads as "zero".
 */

export interface LineSeries {
  label: string
  /** CSS colour, normally a token: `var(--color-ink)`. */
  color: string
  values: readonly (number | null)[]
}

export function LineChart({
  series,
  labels,
  height = 170,
  ariaLabel,
}: {
  series: readonly LineSeries[]
  /** One per point, e.g. month names. */
  labels: readonly string[]
  height?: number
  ariaLabel: string
}) {
  const id = useId()
  const W = 640
  const H = height
  const PAD_X = 6
  const PAD_TOP = 8
  const PAD_BOTTOM = 24
  const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null))
  const max = all.length ? Math.max(...all) : 0
  const top = max <= 0 ? 1 : max * 1.08
  const n = Math.max(labels.length, 1)
  const x = (i: number) => PAD_X + (n === 1 ? (W - 2 * PAD_X) / 2 : (i * (W - 2 * PAD_X)) / (n - 1))
  const y = (v: number) => PAD_TOP + (1 - v / top) * (H - PAD_TOP - PAD_BOTTOM)

  return (
    <figure className="m-0">
      <svg viewBox={`0 0 ${String(W)} ${String(H)}`} role="img" aria-labelledby={`${id}-t`} className="block h-auto w-full">
        <title id={`${id}-t`}>{ariaLabel}</title>
        {[0, 1, 2, 3].map((g) => {
          const gy = PAD_TOP + (g * (H - PAD_TOP - PAD_BOTTOM)) / 3
          return <line key={g} x1={0} x2={W} y1={gy} y2={gy} stroke="var(--color-line)" strokeWidth={1} />
        })}
        {series.map((s) => {
          let d = ''
          s.values.forEach((v, i) => {
            if (v === null) return
            d += `${d === '' ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`
          })
          return d ? (
            <path key={s.label} d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          ) : null
        })}
        {labels.map((l, i) => (
          <text
            key={`${l}-${String(i)}`}
            x={x(i)}
            y={H - 6}
            /* The end labels anchor inward so they are never clipped at the edges. */
            textAnchor={labels.length > 1 && i === 0 ? 'start' : labels.length > 1 && i === labels.length - 1 ? 'end' : 'middle'}
            fontSize={11.5}
            fill="var(--color-muted)"
            fontFamily="var(--font-dash)"
          >
            {l}
          </text>
        ))}
      </svg>
      {all.length === 0 ? <figcaption className="pt-1 text-[12px] text-muted">No data in this period yet.</figcaption> : null}
    </figure>
  )
}

export function Legend({ items }: { items: readonly { label: string; color: string }[] }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-3.5 gap-y-1">
      {items.map((it) => (
        <li key={it.label} className="flex items-center gap-1.5 text-[12px] text-ink-soft">
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-[3px]" style={{ background: it.color }} />
          {it.label}
        </li>
      ))}
    </ul>
  )
}

export interface Segment {
  label: string
  value: number
  color: string
}

/** One bar split by share ("Nursery 1,120 · LKG 1,840 · UKG 1,900"). */
export function StackedBar({ segments, ariaLabel }: { segments: readonly Segment[]; ariaLabel: string }) {
  const total = segments.reduce((a, s) => a + Math.max(0, s.value), 0)
  return (
    <div role="img" aria-label={ariaLabel} className="flex h-1.5 w-full overflow-hidden rounded-full bg-surface-muted">
      {total > 0
        ? segments.map((s) => (
            <span key={s.label} className="h-full" style={{ width: `${String((Math.max(0, s.value) / total) * 100)}%`, background: s.color }} />
          ))
        : null}
    </div>
  )
}

/** A funnel step: label, bar against the first step, count, conversion. */
export function FunnelRow({
  label,
  value,
  max,
  conversion,
}: {
  label: string
  value: number
  /** The first step's value — every bar is drawn against it. */
  max: number
  /** Already formatted ("68%"), or null for the first step. */
  conversion: string | null
}) {
  const pct = max > 0 ? Math.min(1, Math.max(0, value / max)) * 100 : 0
  return (
    <li className="grid grid-cols-[minmax(0,10rem)_1fr_2.5rem_2.75rem] items-center gap-3 py-1.5">
      <span className="truncate text-[13px] text-ink">{label}</span>
      <span className="h-5 overflow-hidden rounded-full bg-surface-muted">
        <span className="block h-full rounded-full bg-primary" style={{ width: `${String(pct)}%` }} />
      </span>
      <span className="tabular text-right text-[13px] font-medium text-ink">{value.toLocaleString('en-IN')}</span>
      <span className="tabular text-right text-[11.5px] text-muted">{conversion ?? ''}</span>
    </li>
  )
}
